import React, { useMemo } from 'react';
import { BulkMessageType } from '../../hooks/useBulkMessaging';

export interface ProgressItemView {
  userId: number;
  phone: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  message_id?: string;
}

interface ProgressPanelProps {
  open: boolean;
  onClose?: () => void;
  type: BulkMessageType;
  items: ProgressItemView[];
  currentBatch: number;
  totalBatches: number;
  running: boolean;
  summary: { success: number; error: number; total: number };
  onRetryFailed: () => void;
  onCancel: () => void;
}

export const ProgressPanel: React.FC<ProgressPanelProps> = ({ open, onClose, type, items, currentBatch, totalBatches, running, summary, onRetryFailed, onCancel }) => {
  const percent = useMemo(() => summary.total ? Math.round(((summary.success + summary.error) / summary.total) * 100) : 0, [summary]);
  const hasFailures = summary.error > 0;
  if (!open) return null;

  return (
    <div style={{ position: 'fixed', right: 16, bottom: 16, width: 360, background: '#fff', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontFamily: 'sans-serif', zIndex: 999 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>전송 진행 상태</strong>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>✕</button>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 12, marginBottom: 4 }}>채널: {type}</div>
        {totalBatches > 0 && (
          <div style={{ fontSize: 12, marginBottom: 4 }}>배치: {currentBatch}/{totalBatches}</div>
        )}
        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', margin: '6px 0 12px' }}>
          <div style={{ width: percent + '%', background: '#2563eb', height: '100%', transition: 'width .3s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
          <span>성공: {summary.success}</span>
          <span style={{ color: hasFailures ? '#dc2626' : undefined }}>실패: {summary.error}</span>
          <span>총: {summary.total}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {running && <button type="button" onClick={onCancel}>중단</button>}
          {!running && hasFailures && <button type="button" onClick={onRetryFailed}>실패 재시도</button>}
        </div>
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={th}>번호</th>
                <th style={th}>상태</th>
                <th style={th}>ID</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 200).map((r,i) => (
                <tr key={r.userId} style={{ background: rowBg(r.status) }}>
                  <td style={td}>{r.phone}</td>
                  <td style={td}>{statusLabel(r.status)}</td>
                  <td style={td}>{r.message_id || (r.status === 'error' ? '—' : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length > 200 && <div style={{ fontSize: 11, marginTop: 4, color: '#666' }}>최대 200건만 표시 중...</div>}
      </div>
    </div>
  );
};

const statusLabel = (s: 'pending' | 'success' | 'error') => s === 'pending' ? '대기' : s === 'success' ? '성공' : '실패';
const rowBg = (s: 'pending' | 'success' | 'error') => s === 'pending' ? '#fff' : s === 'success' ? '#ecfdf5' : '#fef2f2';
const th: React.CSSProperties = { padding: '4px 6px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontWeight: 600 };
const td: React.CSSProperties = { padding: '4px 6px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };

export default ProgressPanel;
